/**
 * Metrics mode (§5.1 #24, PRD Metrics mode; redesign § Metrics mode).
 *
 * One request returns the sections, each independently `ok` or `empty` with its own reason and
 * tooltip — so an insufficient follower history never blanks the overview. This screen owns two
 * extra parameters: `topMetric`, and the persisted bucket-granularity override the view passes
 * down (`undefined` = the server auto-picks per §8.5). Posting schedule and best times render as
 * one merged Schedule section; the time-series chart lives in Overview (visual placement only —
 * the data stays in the `postingSchedule` DTO section).
 *
 * It also owns the day-posts popup: clicking a chart bucket or a calendar square opens the feed
 * narrowed to that bucket. The bounds come from the response (`meta.range` and the neighbouring
 * bucket's start, never `t + 24 h` — a DST day is 23 or 25 hours long), and the type list from
 * `meta.countedPostTypes`, so the popup lists exactly what the mark counted.
 */

import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  BUCKET_GRANULARITIES,
  type BucketGranularity,
  type MetricKey,
  type MetricsQuery,
  type PostType,
  type ScheduleHeatmapMode,
  type SubjectType,
  type ViewFilterParams,
  type ViewFiltersState,
} from '@lw/shared';
import { getViewMetrics } from '../../../api/views';
import { QK } from '../../../api/queryKeys';
import { ErrorState, Spinner } from '../../../components';
import { useMediaEnabled } from '../../settings/useSettings';
import { BucketPostsDialog } from './BucketPostsDialog';
import { FollowerGrowthSection } from './FollowerGrowthSection';
import { OverviewSection } from './OverviewSection';
import {
  DEFAULT_PER_MEMBER_SORT,
  nextPerMemberSort,
  type PerMemberSort,
  type PerMemberSortKey,
} from './PerMemberTable';
import { toggleAuthorId } from '../viewState';
import { ScheduleSection } from './ScheduleSection';
import { SectionCard } from './SectionCard';
import { TopPostsMetricSelect, TopPostsSection } from './TopPostsSection';
import { TypeBreakdownSection } from './TypeBreakdownSection';
import './metrics.css';

const GRANULARITY_LABEL: Record<BucketGranularity, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
};

/** What the day-posts popup needs: a server-built title and the bucket's own bounds. */
type BucketPopup = { title: string; from: number; to: number };

export type MetricsModeProps = {
  subjectType: SubjectType;
  id: number;
  params: Partial<ViewFilterParams>;
  enabled: boolean;
  timezone: string;
  /** Persisted per-view override; `undefined` = the server's auto-pick (§8.5). */
  granularity?: BucketGranularity;
  onGranularityChange: (granularity: BucketGranularity) => void;
  /** Persisted per-view Schedule faces; `undefined` = `volume` (§8.6). */
  calendarMode?: ScheduleHeatmapMode;
  matrixMode?: ScheduleHeatmapMode;
  onCalendarModeChange: (mode: ScheduleHeatmapMode) => void;
  onMatrixModeChange: (mode: ScheduleHeatmapMode) => void;
  /** The view's persisted filters — the per-member table toggles `authorIds` in them. */
  filters: ViewFiltersState;
  onFiltersChange: (filters: ViewFiltersState) => void;
};

export function MetricsMode({
  subjectType,
  id,
  params,
  enabled,
  timezone,
  granularity,
  onGranularityChange,
  calendarMode,
  matrixMode,
  onCalendarModeChange,
  onMatrixModeChange,
  filters,
  onFiltersChange,
}: MetricsModeProps) {
  const [topMetric, setTopMetric] = useState<MetricKey>('reactions');
  const [popup, setPopup] = useState<BucketPopup | null>(null);
  // The per-member table's own view state lives here, not in `OverviewSection`: a row click
  // changes the filters, which refetches, and the section unmounts behind the pending state.
  const [perMemberSort, setPerMemberSort] = useState<PerMemberSort>(DEFAULT_PER_MEMBER_SORT);
  const [perMemberFullscreen, setPerMemberFullscreen] = useState(false);
  const mediaEnabled = useMediaEnabled();

  const query: Partial<MetricsQuery> = { ...params, topMetric, granularity };
  const metrics = useQuery({
    queryKey: QK.viewMetrics(subjectType, id, query),
    queryFn: ({ signal }) => getViewMetrics(subjectType, id, query, signal),
    enabled,
    // A member toggle is a new query key; without this the whole screen (the fullscreen popup
    // included) would be torn down and rebuilt behind the spinner on every click.
    placeholderData: keepPreviousData,
  });

  if (metrics.isPending) return <Spinner size="lg" label="Computing metrics…" />;

  if (metrics.isError) {
    return (
      <ErrorState
        title="Couldn't load metrics"
        error={metrics.error}
        onRetry={() => void metrics.refetch()}
        retrying={metrics.isFetching}
      />
    );
  }

  const data = metrics.data;
  const meta = data.meta;
  const schedule = data.postingSchedule?.state === 'ok' ? data.postingSchedule.data : null;
  const series = schedule?.series ?? [];
  const days = schedule?.heatmap.days ?? [];

  // Exactly what the chart counted: the counted-types setting the server reported in `meta`
  // (not the live settings cache — a toggle mid-view must not desync the popup from the marks),
  // narrowed by the post-type filter when one is active.
  const popupPostTypes: PostType[] =
    params.postTypes === undefined
      ? meta.countedPostTypes
      : meta.countedPostTypes.filter((type) => params.postTypes?.includes(type) === true);

  /** A bucket ends where the next one starts; the last one ends with the reported range. */
  const openBucket = (index: number) => {
    const bucket = series[index];
    if (bucket === undefined || bucket.posts === 0 || popupPostTypes.length === 0) return;
    setPopup({
      title: bucket.fullLabel,
      // The first bucket starts at its calendar boundary, which can precede the range.
      from: Math.max(bucket.t, meta.range.from),
      to: (series[index + 1]?.t ?? meta.range.to + 1) - 1,
    });
  };

  const openDay = (index: number) => {
    const day = days[index];
    if (day === undefined || day.posts === 0 || popupPostTypes.length === 0) return;
    setPopup({
      title: day.label,
      from: Math.max(day.t, meta.range.from),
      to: (days[index + 1]?.t ?? meta.range.to + 1) - 1,
    });
  };

  return (
    <div className="metrics">
      <SectionCard
        title="Overview"
        section={data.overview}
        actions={
          <select
            className="select"
            aria-label="Chart granularity"
            // meta.granularity is what the server actually used: the override is best-effort and
            // degrades over long ranges (§8.5), so the stored value alone could lie.
            value={data.meta.granularity}
            onChange={(event) => onGranularityChange(event.target.value as BucketGranularity)}
          >
            {BUCKET_GRANULARITIES.map((option) => (
              <option key={option} value={option}>
                {GRANULARITY_LABEL[option]}
              </option>
            ))}
          </select>
        }
      >
        {(overview) => (
          <OverviewSection
            data={overview}
            schedule={data.postingSchedule}
            onBucketClick={openBucket}
            mediaEnabled={mediaEnabled}
            activeAuthorIds={filters.authorIds ?? []}
            onToggleMember={(memberId) => onFiltersChange(toggleAuthorId(filters, memberId))}
            perMemberSort={perMemberSort}
            onPerMemberSort={(key: PerMemberSortKey) =>
              setPerMemberSort((current) => nextPerMemberSort(current, key))
            }
            perMemberFullscreen={perMemberFullscreen}
            onPerMemberFullscreenChange={setPerMemberFullscreen}
          />
        )}
      </SectionCard>

      <ScheduleSection
        schedule={data.postingSchedule}
        bestTimes={data.bestTimes}
        timezone={timezone}
        calendarMode={calendarMode ?? 'volume'}
        matrixMode={matrixMode ?? 'volume'}
        onCalendarModeChange={onCalendarModeChange}
        onMatrixModeChange={onMatrixModeChange}
        onDayClick={openDay}
      />

      <SectionCard title="Follower growth" section={data.followerGrowth}>
        {(growth) => <FollowerGrowthSection data={growth} />}
      </SectionCard>

      <SectionCard title="Post & content type breakdown" section={data.typeBreakdown}>
        {(breakdown) => <TypeBreakdownSection data={breakdown} />}
      </SectionCard>

      <SectionCard
        title="Top posts"
        section={data.topPosts}
        actions={<TopPostsMetricSelect value={topMetric} onChange={setTopMetric} />}
      >
        {(top) => <TopPostsSection data={top} />}
      </SectionCard>

      {popup !== null ? (
        <BucketPostsDialog
          subjectType={subjectType}
          id={id}
          baseParams={params}
          title={popup.title}
          from={popup.from}
          to={popup.to}
          postTypes={popupPostTypes}
          mediaEnabled={mediaEnabled}
          onClose={() => setPopup(null)}
        />
      ) : null}
    </div>
  );
}

export default MetricsMode;
